/**
 * Safety Tips Page
 * Route: /safety
 * Features: Trust header, Buyer guidelines, Seller guidelines, Payment safety, Reporting instructions
 */

import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import {
  ArrowLeft,
  ShieldCheck,
  Eye,
  DollarSign,
  AlertTriangle,
  UserCheck,
  MessageSquare,
} from 'lucide-react';

const SafetyTipsPage: React.FC = () => {
  const buyerTips = [
    {
      title: 'Meet in Public Places',
      desc: 'Always arrange to meet the seller in a busy, well-lit public place such as a shopping mall, metro station, or coffee shop. Avoid meeting in isolated locations or private residences.',
      icon: <UserCheck className="h-5 w-5 text-green-500" />,
    },
    {
      title: 'Inspect the Item Thoroughly',
      desc: 'Never buy sight unseen. Inspect the item carefully to verify its condition, authenticity, and functionality. Test electronic items, check vehicles with a mechanic, and try on apparel.',
      icon: <Eye className="h-5 w-5 text-blue-500" />,
    },
    {
      title: 'Use Secure Payment Methods',
      desc: 'Avoid sending money in advance. Pay only after you have inspected the item and are satisfied with it. Cash is generally safe, or use secure instant UPI transfer while face-to-face.',
      icon: <DollarSign className="h-5 w-5 text-amber-500" />,
    },
  ];

  const sellerTips = [
    {
      title: 'Verify Buyer Information',
      desc: 'If a buyer is acting suspiciously, making unusually quick offers, or asking to communicate off-platform immediately, be cautious. Stick to in-app messaging for security.',
      icon: <ShieldCheck className="h-5 w-5 text-indigo-500" />,
    },
    {
      title: 'Avoid Shipments before Payment',
      desc: 'Never mail or ship items before payment has been verified. Beware of fake bank notifications, check payment receipts, or screenshot confirmations.',
      icon: <DollarSign className="h-5 w-5 text-rose-500" />,
    },
    {
      title: 'Meet in Safe Areas',
      desc: 'Just like buyers, sellers should also prioritize public meeting spaces. If selling a heavy item (like furniture) from your home, try to have a friend or family member present.',
      icon: <UserCheck className="h-5 w-5 text-emerald-500" />,
    },
  ];

  return (
    <div className="min-h-screen bg-background" id="safety-tips-page">
      <SEO
        title="Safety Tips"
        description="Learn how to buy and sell safely on MemAds. Essential safety advice, scam prevention, and secure transaction guidelines."
        canonical="/safety"
      />
      {/* Hero Banner */}
      <section className="relative bg-gradient-to-r from-teal-800 to-accent py-16 px-4 text-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.05),transparent_45%)]" />
        <div className="relative max-w-2xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
          <span className="grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white mx-auto mb-4">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-white">Trust & Safety Guidelines</h1>
          <p className="text-sm text-white/80 mt-2">Your safety is our top priority. Follow these tips for secure local trading.</p>
        </div>
      </section>

      {/* Main Body */}
      <main className="max-w-4xl mx-auto px-4 py-12 space-y-12">
        {/* Urgent Warning Banner */}
        <section className="bg-red-500/10 border border-red-500/20 rounded-2xl p-5 flex gap-4 items-start">
          <AlertTriangle className="h-6 w-6 text-destructive flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-heading text-sm font-bold text-destructive">Crucial Rule: No Advance Payments</h2>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Never send money online to a seller before inspecting the item in person. We advise against paying deposit fees, shipping fees, or reserving items via bank transfer. If a seller insists on advance payment, cancel the transaction immediately.
            </p>
          </div>
        </section>

        {/* Buyer Section */}
        <section>
          <h2 className="font-heading text-xl font-bold text-foreground border-b border-border pb-3 mb-6">Tips for Buyers</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {buyerTips.map((tip) => (
              <div key={tip.title} className="bg-card border border-border rounded-2xl p-5 shadow-sm">
                <div className="mb-4 inline-block rounded-xl bg-secondary p-2.5">
                  {tip.icon}
                </div>
                <h3 className="font-heading text-sm font-bold text-foreground mb-2">{tip.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{tip.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Seller Section */}
        <section>
          <h2 className="font-heading text-xl font-bold text-foreground border-b border-border pb-3 mb-6">Tips for Sellers</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {sellerTips.map((tip) => (
              <div key={tip.title} className="bg-card border border-border rounded-2xl p-5 shadow-sm">
                <div className="mb-4 inline-block rounded-xl bg-secondary p-2.5">
                  {tip.icon}
                </div>
                <h3 className="font-heading text-sm font-bold text-foreground mb-2">{tip.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{tip.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Reporting Section */}
        <section className="bg-card border border-border rounded-2xl p-6 shadow-sm flex flex-col md:flex-row gap-6 items-center">
          <div className="flex-1">
            <h2 className="font-heading text-base font-bold text-foreground flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-primary" />
              Report Suspicious Activity
            </h2>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              If you encounter a listing or user that you suspect is fraudulent, copy their listing link and report it immediately. Our moderating team reviews reports continuously and suspends accounts violating safety standards.
            </p>
          </div>
          <div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-5 py-3 text-xs font-semibold text-foreground hover:border-primary hover:text-primary transition"
            >
              <MessageSquare className="h-4 w-4" />
              Report to Support
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SafetyTipsPage;
